/**
 * sportsConfig.js — Single source of truth for sport/category configuration
 *
 * Centralizes:
 *   - Labels (pt-BR display + slug)
 *   - Colors (gradient, accent, text)
 *   - Emoji / icon
 *   - Banner visual config (gradient + pattern)
 *
 * Accepts BOTH backend keys (swimming, football) and frontend slugs (natacao, futebol).
 *
 * Usage:
 *   import { getSport, getSportGradient, SPORT_MAP } from '../config/sportsConfig';
 *
 *   const cfg = getSport('swimming');   // → { label: 'Natação', ... }
 *   const cfg2 = getSport('natacao');   // → same object
 *   const grad = getSportGradient('football'); // → CSS gradient string
 */

// ─── Master definitions ─────────────────────────────────

const SPORTS = {
  swimming: {
    id: 'swimming',
    slug: 'natacao',
    label: 'Natação',
    labelUpper: 'NATAÇÃO',
    emoji: '🏊',
    sublabel: 'Data Study',
    accent: '#0369a1',
    gradient: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 40%, #0ea5e9 100%)',
    gradientBar: 'linear-gradient(90deg, #0c4a6e, #0ea5e9)',
    pattern: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 2px, transparent 2px, transparent 40px)',
  },
  football: {
    id: 'football',
    slug: 'futebol',
    label: 'Futebol',
    labelUpper: 'FUTEBOL',
    emoji: '⚽',
    sublabel: 'Data Study',
    accent: '#047857',
    gradient: 'linear-gradient(135deg, #064e3b 0%, #047857 40%, #10b981 100%)',
    gradientBar: 'linear-gradient(90deg, #064e3b, #10b981)',
    pattern: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 12px)',
  },
  basketball: {
    id: 'basketball',
    slug: 'basquete',
    label: 'Basquete',
    labelUpper: 'BASQUETE',
    emoji: '🏀',
    sublabel: 'Data Study',
    accent: '#ea580c',
    gradient: 'linear-gradient(135deg, #9a3412 0%, #ea580c 40%, #fb923c 100%)',
    gradientBar: 'linear-gradient(90deg, #9a3412, #fb923c)',
    pattern: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.04) 0%, transparent 60%)',
  },
  athletics: {
    id: 'athletics',
    slug: 'atletismo',
    label: 'Atletismo',
    labelUpper: 'ATLETISMO',
    emoji: '🏃',
    sublabel: 'Data Study',
    accent: '#dc2626',
    gradient: 'linear-gradient(135deg, #991b1b 0%, #dc2626 40%, #f87171 100%)',
    gradientBar: 'linear-gradient(90deg, #991b1b, #f87171)',
    pattern: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 3px, transparent 3px, transparent 20px)',
  },
  gym: {
    id: 'gym',
    slug: 'academia',
    label: 'Academia',
    labelUpper: 'ACADEMIA',
    emoji: '🏋️',
    sublabel: 'Data Study',
    accent: '#6b7280',
    gradient: 'linear-gradient(135deg, #374151 0%, #6b7280 40%, #9ca3af 100%)',
    gradientBar: 'linear-gradient(90deg, #374151, #9ca3af)',
    pattern: 'none',
  },
  multi: {
    id: 'multi',
    slug: 'multi',
    label: 'Multiesporte',
    labelUpper: 'MULTIESPORTE',
    emoji: '🏅',
    sublabel: 'Data Analysis',
    accent: '#7c3aed',
    gradient: 'linear-gradient(135deg, #581c87 0%, #7c3aed 40%, #a78bfa 100%)',
    gradientBar: 'linear-gradient(90deg, #581c87, #a78bfa)',
    pattern: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.05) 0%, transparent 50%)',
  },
};

// Default/fallback for unknown categories
const DEFAULT_SPORT = {
  id: 'general',
  slug: 'geral',
  label: 'Geral',
  labelUpper: 'DATA STUDY',
  emoji: '📊',
  sublabel: 'VUKZ Intelligence',
  accent: '#374151',
  gradient: 'linear-gradient(135deg, #111111 0%, #374151 100%)',
  gradientBar: 'linear-gradient(90deg, #111, #374151)',
  pattern: 'none',
};

// ─── Alias map (maps any known key → canonical sport key) ─

const ALIAS_MAP = {
  // Backend keys (English)
  swimming: 'swimming',
  football: 'football',
  basketball: 'basketball',
  athletics: 'athletics',
  gym: 'gym',
  multi: 'multi',
  general: 'general',

  // Frontend slugs (Portuguese)
  natacao: 'swimming',
  'natação': 'swimming',
  futebol: 'football',
  basquete: 'basketball',
  atletismo: 'athletics',
  academia: 'gym',
  multiesporte: 'multi',
  geral: 'general',

  // Common variations
  swim: 'swimming',
  soccer: 'football',
  futsal: 'football',
};

// ─── Public API ─────────────────────────────────────────

/**
 * Get full sport config by any key (backend id, frontend slug, Portuguese name).
 * Always returns a valid config (fallback to DEFAULT_SPORT).
 */
export const getSport = (key) => {
  if (!key) return DEFAULT_SPORT;
  const normalized = String(key).toLowerCase().trim();
  const canonical = ALIAS_MAP[normalized];
  return canonical ? (SPORTS[canonical] || DEFAULT_SPORT) : DEFAULT_SPORT;
};

/**
 * Get CSS gradient string for a sport.
 */
export const getSportGradient = (key) => getSport(key).gradient;

/**
 * Get CSS bar gradient (horizontal, for thin accent bars).
 */
export const getSportBarGradient = (key) => getSport(key).gradientBar;

/**
 * Get sport label in pt-BR.
 */
export const getSportLabel = (key) => getSport(key).label;

/**
 * Get sport emoji.
 */
export const getSportEmoji = (key) => getSport(key).emoji;

/**
 * Check if a key maps to a known sport.
 */
export const isKnownSport = (key) => {
  if (!key) return false;
  return ALIAS_MAP[String(key).toLowerCase().trim()] !== undefined;
};

/**
 * Full map for iteration (e.g., filter dropdowns).
 * Returns only the canonical SPORTS (not aliases).
 */
export const SPORT_MAP = SPORTS;

/**
 * Ordered list for UI dropdowns.
 */
export const SPORT_LIST = [
  SPORTS.swimming,
  SPORTS.football,
  SPORTS.basketball,
  SPORTS.athletics,
  SPORTS.gym,
  SPORTS.multi,
];

export default getSport;

// ─── Swimming Category Configuration ─────────────────────
// Canonical age categories for swimming in chronological development order.
// Matches dim_swim_category in the database.
// Categories are DERIVED from competition name + birth_date (CBDA age rules).
// 7 PRIMARY categories (shown in charts) + 3 GENERIC fallbacks (no birth_date).

export const SWIM_CATEGORY_ORDER = [
  'INFANTIL 1',
  'INFANTIL 2',
  'JUVENIL 1',
  'JUVENIL 2',
  'JUNIOR 1',
  'JUNIOR 2',
  'ABSOLUTO',
];

// Full order including generic fallbacks (for sort comparisons)
const SWIM_CATEGORY_FULL_ORDER = [
  'INFANTIL 1',   // sort 10
  'INFANTIL 2',   // sort 20
  'INFANTIL',     // sort 25 (generic, no birth_date)
  'JUVENIL 1',    // sort 30
  'JUVENIL 2',    // sort 40
  'JUVENIL',      // sort 45 (generic)
  'JUNIOR 1',     // sort 50
  'JUNIOR 2',     // sort 60
  'JUNIOR',       // sort 65 (generic)
  'ABSOLUTO',     // sort 70
];

export const SWIM_CATEGORY_SORT = Object.fromEntries(
  SWIM_CATEGORY_FULL_ORDER.map((key, idx) => [key, (idx + 1) * 10])
);

export const SWIM_CATEGORY_COLORS = {
  'INFANTIL 1':  '#3b82f6',   // blue-500
  'INFANTIL 2':  '#60a5fa',   // blue-400
  'INFANTIL':    '#93c5fd',   // blue-300 (generic fallback)
  'JUVENIL 1':   '#10b981',   // emerald-500
  'JUVENIL 2':   '#34d399',   // emerald-400
  'JUVENIL':     '#6ee7b7',   // emerald-300 (generic fallback)
  'JUNIOR 1':    '#f59e0b',   // amber-500
  'JUNIOR 2':    '#fbbf24',   // amber-400
  'JUNIOR':      '#fde68a',   // amber-200 (generic fallback)
  'ABSOLUTO':    '#111111',   // near-black
};

export const SWIM_CATEGORY_DISPLAY = {
  'INFANTIL 1':  'Infantil 1',
  'INFANTIL 2':  'Infantil 2',
  'INFANTIL':    'Infantil',
  'JUVENIL 1':   'Juvenil 1',
  'JUVENIL 2':   'Juvenil 2',
  'JUVENIL':     'Juvenil',
  'JUNIOR 1':    'Junior 1',
  'JUNIOR 2':    'Junior 2',
  'JUNIOR':      'Junior',
  'ABSOLUTO':    'Absoluto',
};

/**
 * Get color for a swimming age category (case-insensitive).
 * Falls back to gray if unknown.
 */
export const getSwimCategoryColor = (cat) => {
  if (!cat) return '#9ca3af';
  const upper = cat.toUpperCase().trim();
  return SWIM_CATEGORY_COLORS[upper] || '#9ca3af';
};

/**
 * Sort an array of category names by chronological development order.
 */
export const sortSwimCategories = (categories) => {
  return [...categories].sort((a, b) => {
    const aOrder = SWIM_CATEGORY_SORT[a?.toUpperCase?.().trim()] ?? 99;
    const bOrder = SWIM_CATEGORY_SORT[b?.toUpperCase?.().trim()] ?? 99;
    return aOrder - bOrder;
  });
};
